import { useRef, useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { Check, AlertCircle, Languages, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * Язык для отображения
 */
export interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag?: string;
  flagEmoji?: string;
}

/**
 * Статус заполненности языка
 */
export type FillStatus = 'complete' | 'partial' | 'empty';

/**
 * Информация о заполненности
 */
export interface LanguageFillInfo {
  status: FillStatus;
  filled: number;
  total: number;
  hasErrors?: boolean;
}

interface LanguageScrollTabsProps {
  /** Список всех языков */
  languages: Language[];
  /** Активный язык */
  activeLanguage: string;
  /** Колбэк смены языка */
  onLanguageChange: (code: string) => void;
  /** Информация о заполненности по языкам */
  fillStatus?: Record<string, LanguageFillInfo>;
  /** Видимые языки (для фильтрации) */
  visibleLanguages?: string[];
  /** Колбэк изменения видимых языков */
  onVisibleLanguagesChange?: (codes: string[]) => void;
  /** Показывать кнопку выбора языков */
  showLanguageSelector?: boolean;
  /** Показывать горизонтальные табы с флагами */
  showTabs?: boolean;
  /** Класс для контейнера */
  className?: string;
  /** Размер табов */
  size?: 'sm' | 'md';
}

const SCROLL_AMOUNT = 200;

/**
 * Горизонтальный скролл табов языков с индикацией заполненности.
 * Переиспользуемый компонент для форм с переводами.
 */
export function LanguageScrollTabs({
  languages,
  activeLanguage,
  onLanguageChange,
  fillStatus,
  visibleLanguages,
  onVisibleLanguagesChange,
  showLanguageSelector = true,
  showTabs = true,
  className,
  size = 'md',
}: LanguageScrollTabsProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Определяем отображаемые языки
  const displayLanguages = visibleLanguages?.length
    ? languages.filter((l) => visibleLanguages.includes(l.code))
    : languages;

  // Проверка возможности скролла
  const checkScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) {return;}
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll, displayLanguages]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) {return;}
    el.scrollBy({
      left: direction === 'left' ? -SCROLL_AMOUNT : SCROLL_AMOUNT,
      behavior: 'smooth',
    });
  };

  const handleSelectAll = () => {
    onVisibleLanguagesChange?.(languages.map((l) => l.code));
  };

  const handleDeselectAll = () => {
    onVisibleLanguagesChange?.([]);
  };

  const handleToggleLanguage = (code: string) => {
    if (!onVisibleLanguagesChange) {return;}
    const current = visibleLanguages ?? languages.map((l) => l.code);
    if (current.includes(code)) {
      onVisibleLanguagesChange(current.filter((c) => c !== code));
    } else {
      onVisibleLanguagesChange([...current, code]);
    }
  };

  const getFlag = (lang: Language) => lang.flag || lang.flagEmoji || '🏳️';

  const getTooltipText = (lang: Language, status?: LanguageFillInfo) => {
    let text = lang.nativeName;
    if (status) {
      if (status.status === 'complete') {text += ' — Все поля заполнены';}
      else if (status.status === 'partial') {text += ` — Заполнено: ${status.filled} из ${status.total}`;}
      else if (status.status === 'empty') {text += ' — Не заполнено';}
      if (status.hasErrors) {text += ' (есть ошибки)';}
    }
    return text;
  };

  const sizeClasses = {
    sm: 'px-2.5 py-1 text-xs gap-1',
    md: 'px-3 py-1.5 text-sm gap-1.5',
  };

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {showTabs && (
        <>
          {/* Scroll Left Button */}
          {canScrollLeft && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => scroll('left')}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
          )}

          {/* Language Tabs */}
          <div
            ref={scrollRef}
            onScroll={checkScroll}
            className="flex gap-1 overflow-x-auto scrollbar-hide flex-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {displayLanguages.map((lang) => {
              const status = fillStatus?.[lang.code];
              const isActive = lang.code === activeLanguage;

              return (
                <button
                  key={lang.code}
                  onClick={() => onLanguageChange(lang.code)}
                  title={getTooltipText(lang, status)}
                  className={cn(
                    'inline-flex items-center rounded-md font-medium transition-colors border whitespace-nowrap shrink-0',
                    'hover:bg-accent hover:text-accent-foreground',
                    'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                    sizeClasses[size],
                    isActive
                      ? 'bg-primary text-primary-foreground border-primary hover:bg-primary/90 hover:text-primary-foreground'
                      : 'bg-background border-input',
                    status?.hasErrors && !isActive && 'border-destructive'
                  )}
                >
                  <span>{getFlag(lang)}</span>
                  <span className="hidden sm:inline">{lang.nativeName}</span>
                  <span className="sm:hidden uppercase">{lang.code}</span>
                  <FillStatusIndicator status={status} isActive={isActive} size={size} />
                </button>
              );
            })}
          </div>

          {/* Scroll Right Button */}
          {canScrollRight && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => scroll('right')}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </>
      )}

      {/* Language Selector Dropdown */}
      {showLanguageSelector && onVisibleLanguagesChange && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="shrink-0">
              <Languages className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Языки</span>
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-[10px]">
                {displayLanguages.length}
              </Badge>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="flex gap-2 p-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 text-xs"
                onClick={handleSelectAll}
              >
                Все
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex-1 text-xs"
                onClick={handleDeselectAll}
              >
                Очистить
              </Button>
            </div>
            <DropdownMenuSeparator />
            {languages.map((lang) => {
              const isVisible =
                !visibleLanguages?.length || visibleLanguages.includes(lang.code);
              const status = fillStatus?.[lang.code];

              return (
                <DropdownMenuCheckboxItem
                  key={lang.code}
                  checked={isVisible}
                  onCheckedChange={() => handleToggleLanguage(lang.code)}
                  onSelect={(e) => e.preventDefault()}
                >
                  <span className="mr-2">{getFlag(lang)}</span>
                  <span className="flex-1">{lang.name}</span>
                  {status && (
                    <FillStatusIndicator status={status} size="sm" />
                  )}
                </DropdownMenuCheckboxItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

/**
 * Индикатор статуса заполненности
 */
function FillStatusIndicator({
  status,
  isActive = false,
  size = 'md',
}: {
  status?: LanguageFillInfo;
  isActive?: boolean;
  size?: 'sm' | 'md';
}) {
  if (!status) {return null;}

  if (status.hasErrors) {
    return (
      <AlertCircle
        className={cn(
          size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5',
          isActive ? 'text-primary-foreground' : 'text-destructive'
        )}
      />
    );
  }

  if (status.status === 'complete') {
    return (
      <Check
        className={cn(
          size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5',
          isActive ? 'text-primary-foreground' : 'text-green-500'
        )}
      />
    );
  }

  if (status.status === 'partial') {
    return (
      <Badge
        variant="secondary"
        className={cn(
          'font-normal',
          size === 'sm' ? 'h-3.5 px-1 text-[9px]' : 'h-4 px-1 text-[10px]',
          isActive
            ? 'bg-primary-foreground/20 text-primary-foreground'
            : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
        )}
      >
        {status.filled}/{status.total}
      </Badge>
    );
  }

  return (
    <div
      className={cn(
        'rounded-full',
        size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2',
        isActive ? 'bg-primary-foreground/40' : 'bg-muted-foreground/30'
      )}
    />
  );
}

/**
 * Сводка по заполненности всех языков
 */
export function LanguageFillSummary({
  fillStatus,
  languages,
  className,
}: {
  fillStatus: Record<string, LanguageFillInfo>;
  languages: Language[];
  className?: string;
}) {
  const stats = languages.reduce(
    (acc, lang) => {
      const status = fillStatus[lang.code];
      if (status?.status === 'complete') {acc.complete++;}
      else if (status?.status === 'partial') {acc.partial++;}
      else {acc.empty++;}
      if (status?.hasErrors) {acc.hasErrors = true;}
      return acc;
    },
    { complete: 0, partial: 0, empty: 0, hasErrors: false, total: languages.length }
  );

  if (stats.hasErrors) {
    return (
      <Badge variant="destructive" className={cn('text-[10px]', className)}>
        Есть ошибки
      </Badge>
    );
  }

  if (stats.complete === stats.total) {
    return (
      <Badge
        className={cn(
          'text-[10px] bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
          className
        )}
      >
        <Check className="h-3 w-3 mr-1" />
        Все заполнены
      </Badge>
    );
  }

  return (
    <Badge variant="secondary" className={cn('text-[10px]', className)}>
      {stats.complete}/{stats.total} готово
    </Badge>
  );
}
